import { initJsPsych } from "jspsych";
import PropTypes from "prop-types";
import React from "react";

import { config, taskName, taskReleaseLabel, taskVersion } from "../../config/main";
import { buildTimeline, jsPsychOptions } from "../../experiment";
import { initParticipant } from "../deployments/firebase";

import { CustomKeyboardResponse } from "../../gearshiftGame/customKeyboardResponse";
import { enableAiAgentInterface, launchAgentSession } from "../../experiment/procedures/aiAgent";
import { getDebugOverlay } from "../../experiment/ui/debugOverlay";
import { updateDebugLoginInfo, updateDebugMetadataSource } from "../../experiment/ui/debugHelpers";
// import "../../experiment/ui/roadOffsetPanel";
import "../../experiment/ui/highwayDivider";

// ID used to identify the DOM element that holds the experiment.
const EXPERIMENT_ID = "experiment-window";

export default function JsPsychExperiment({
  loginInformation: { studyID, participantID, sessionID },
  metadata,
  dataUpdateFunction,
  dataFinishFunction,
}) {
  const [jsPsych, setJsPsych] = React.useState();
  const [timeline, setTimeline] = React.useState();
  const [startDate, setStartDate] = React.useState();

  /**
   * Create the instance of JsPsych whenever the studyID or participantID changes, which occurs then the user logs in.
   *
   * This instance of jsPsych is passed to any trials that need it when the timeline is built.
   */
  React.useEffect(() => {
    async function initializeJsPsych() {
      // Start date of the experiment - used as the UID of the session
      const startDate = new Date().toISOString();

      // Write the initial record to Firestore
      if (config.USE_FIREBASE) initParticipant(studyID, participantID, startDate);

      // Initialize the instance of jsPsych that will be used on the next render
      const nextJsPsych = initJsPsych({
        // Combine necessary Ecosystem options with custom ones (src/timelines/main.js)
        ...jsPsychOptions,
        display_element: EXPERIMENT_ID,
        on_data_update: (data) => {
          jsPsychOptions.on_data_update && jsPsychOptions.on_data_update(data); // Call custom on_data_update function (if provided)
          // Debug log for data updates - check for task_metadata in the trial data

          // const allTrials = nextJsPsych.data.get().values();
          // console.log("[jsPsych data check - all trials]", allTrials);
          // // Check for trials with task_metadata
          // const trialsWithMetadata = allTrials
          //   .map((trial, index) => ({ trial, index }))
          //   .filter(({ trial }) => trial.task_metadata);
          // if (trialsWithMetadata.length > 0) {
          //   trialsWithMetadata.forEach(({ trial, index }) => {
          //     console.log(`[jsPsych data check - trial with task_metadata at index ${index}]`, trial);
          //   });
          // }
          dataUpdateFunction(data); // Call Ecosystem's on_data_update function
        },
        on_finish: (data) => {
          jsPsychOptions.on_finish && jsPsychOptions.on_finish(data); // Call custom on_finish function (if provided)
          dataFinishFunction(data); // Call Ecosystem's on_finish function
        },
      });

      // Adds experiment data into jsPsych directly. These properties will be added to all trials
      nextJsPsych.data.addProperties({
        task_name: taskName,
        task_version: taskVersion,
        start_date: startDate,
        study_id: studyID,
        participant_id: participantID,
        session_id: sessionID,
        // task_metadata: metadata, // Added manually to first trial of each mission
      });

      nextJsPsych.customKeyboardResponse = new CustomKeyboardResponse(true);

      // Exposes the devtools handle - launchAgentSession() is a separate, explicit step.
      nextJsPsych.aiAgent = enableAiAgentInterface();

      // Auto-launch when the loaded metadata says this session is agent-controlled
      // (metadata.aiAgent.controllerType === "agent") - see aiAgent/README.md. GF04 doesn't
      // pick which agent/model plays - launchAgentSession() connects an unclaimed session and
      // the agent repo declares its own identity in its session.ready reply.
      if (metadata?.aiAgent?.controllerType === "agent") {
        nextJsPsych.aiAgent = launchAgentSession();
      }

      // Initialize debug overlay and register hotkey (Ctrl + Alt + D)
      const debugOverlay = getDebugOverlay();
      debugOverlay.registerToggleHotkey();
      const baseVersionLabel = `${taskReleaseLabel} | ${taskVersion}`;
      debugOverlay.setVersionLabel(baseVersionLabel);

      // Add git branch + short SHA when /version.json is available.
      fetch("/version.json")
        .then((response) => {
          if (!response.ok) return null;
          return response.json();
        })
        .then((versionInfo) => {
          if (!versionInfo) return;

          const rawRef = versionInfo.ref || "";
          const branch = rawRef.replace("refs/heads/", "") || "unknown-branch";
          const shortSha = (versionInfo.sha || "").slice(0, 7);

          if (!shortSha) return;
          debugOverlay.setVersionLabel(`${baseVersionLabel} | ${branch}@${shortSha}`);
        })
        .catch(() => {
          // Keep base version label when git metadata is unavailable.
        });

      nextJsPsych.debugOverlay = debugOverlay;
      nextJsPsych.customKeyboardResponse.setDebugOverlay(debugOverlay);
      // Set login information in debug overlay
      updateDebugLoginInfo(nextJsPsych, studyID, participantID, sessionID);
      updateDebugMetadataSource(nextJsPsych, metadata?.debugMetadataSource || "-");

      setStartDate(startDate);
      setJsPsych(nextJsPsych);
    }

    initializeJsPsych();
  }, [metadata]);

  /** Asynchronously builds the timeline once jsPsych is initialized */
  React.useEffect(() => {
    if (!jsPsych) return; // Early exit when jsPsych hasn't been initialized (initial render)

    async function initializeTimeline() {
      // Build the timeline that will be used on the next render
      const nextTimeline = await buildTimeline(
        jsPsych,
        studyID,
        participantID,
        sessionID,
        metadata,
        startDate
      );
      setTimeline(nextTimeline);
    }
    initializeTimeline();
  }, [jsPsych]);

  /** Runs the experiment once the timeline is initialized */
  React.useEffect(() => {
    if (!timeline) return; // Early exit when timeline hasn't been initialized (initial render)
    jsPsych.run(timeline);
  }, [timeline]);

  return <div id={EXPERIMENT_ID} />;
}

JsPsychExperiment.propTypes = {
  loginInformation: PropTypes.exact({
    studyID: PropTypes.string,
    participantID: PropTypes.string,
    sessionID: PropTypes.string,
  }),
  metadata: PropTypes.object,
  dataUpdateFunction: PropTypes.func,
  dataFinishFunction: PropTypes.func,
};
